/* ==================================================================
   THE RING BY LUX - theme behaviour

   Contains: motion layer, variant picker, cart operations, FAQ,
   gallery, mobile buy bar, header menu.

   Motion is deliberately restrained (intensity 5, not the landing
   page's 9). Every animation here is one of: hierarchy, storytelling,
   feedback, state transition. Nothing runs because it looks cool.

   Theme editor safety: Shopify injects and destroys sections live.
   Every ScrollTrigger is tagged to its section id and killed on
   shopify:section:unload, or stale pin spacers corrupt scroll
   measurement and the editor becomes unusable.
   ================================================================== */

(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  if (gsap && ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  var bySection = Object.create(null);

  function track(id, thing) {
    if (!id) return thing;
    (bySection[id] || (bySection[id] = [])).push(thing);
    return thing;
  }

  function killSection(id) {
    var list = bySection[id];
    if (!list) return;
    list.forEach(function (t) {
      try {
        if (t.scrollTrigger) t.scrollTrigger.kill();
        if (typeof t.kill === "function") t.kill();
      } catch (e) { /* node already removed */ }
    });
    delete bySection[id];
    if (ScrollTrigger) ScrollTrigger.refresh();
  }

  function sectionIdOf(el) {
    var host = el.closest("[data-section-id]");
    return host ? host.getAttribute("data-section-id") : null;
  }

  function money(cents) {
    var fmt = window.shopMoneyFormat || "{{amount}}";
    var amount = (cents / 100).toFixed(2);
    // Shopify money formats vary; handle the common amount tokens.
    if (fmt.indexOf("amount_no_decimals") > -1) {
      amount = Math.round(cents / 100).toString();
    } else if (fmt.indexOf("amount_with_comma_separator") > -1) {
      amount = (cents / 100).toFixed(2).replace(".", ",");
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /* ================================================================
     Smooth scroll
     ================================================================ */

  var lenis = null;

  function initLenis() {
    if (reduced || lenis || !window.Lenis || !gsap) return;
    lenis = new window.Lenis({ lerp: 0.1, smoothWheel: true });
    lenis.on("scroll", function () { if (ScrollTrigger) ScrollTrigger.update(); });
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    // The theme editor scrolls the preview itself. Lenis fighting that
    // makes sections impossible to select.
    if (window.Shopify && window.Shopify.designMode) lenis.stop();
  }

  /* ================================================================
     Reveal. Justification: storytelling, paces a fast scroll.
     ================================================================ */

  function initReveals(root) {
    root = root || document;
    var els = root.querySelectorAll(".reveal:not([data-revealed])");
    Array.prototype.forEach.call(els, function (el) {
      el.setAttribute("data-revealed", "1");
      if (reduced || !gsap || !ScrollTrigger) {
        if (gsap) gsap.set(el, { autoAlpha: 1, y: 0 });
        else { el.style.opacity = 1; el.style.transform = "none"; }
        return;
      }
      track(sectionIdOf(el), gsap.fromTo(el,
        { autoAlpha: 0, y: 40 },
        {
          autoAlpha: 1, y: 0, duration: 0.9, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true }
        }
      ));
    });
  }

  function initStaggers(root) {
    root = root || document;
    var groups = root.querySelectorAll("[data-stagger]:not([data-staggered])");
    Array.prototype.forEach.call(groups, function (group) {
      group.setAttribute("data-staggered", "1");
      var kids = group.children;
      if (!kids.length) return;
      if (reduced || !gsap || !ScrollTrigger) {
        if (gsap) gsap.set(kids, { autoAlpha: 1, y: 0 });
        return;
      }
      var amount = parseFloat(group.getAttribute("data-stagger")) || 0.07;
      track(sectionIdOf(group), gsap.fromTo(kids,
        { autoAlpha: 0, y: 40 },
        {
          autoAlpha: 1, y: 0, duration: 0.9, ease: "power3.out", stagger: amount,
          scrollTrigger: { trigger: group, start: "top 85%", once: true }
        }
      ));
    });
  }

  /* Timeline dots light as they pass. Justification: hierarchy. */
  function initTimeline(root) {
    root = root || document;
    var rows = root.querySelectorAll(".timeline__row:not([data-lit-bound])");
    Array.prototype.forEach.call(rows, function (row) {
      row.setAttribute("data-lit-bound", "1");
      if (reduced || !ScrollTrigger) { row.classList.add("is-lit"); return; }
      track(sectionIdOf(row), ScrollTrigger.create({
        trigger: row,
        start: "top 75%",
        once: true,
        onEnter: function () { row.classList.add("is-lit"); }
      }));
    });
  }

  /* ================================================================
     Header menu
     ================================================================ */

  function initHeader(root) {
    root = root || document;
    var header = root.querySelector(".header");
    if (!header || header.hasAttribute("data-bound")) return;
    header.setAttribute("data-bound", "1");
    var toggle = header.querySelector(".header__toggle");
    if (!toggle) return;
    toggle.addEventListener("click", function () {
      var open = header.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
    header.querySelectorAll(".header__nav a").forEach(function (a) {
      a.addEventListener("click", function () {
        header.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
      });
    });
  }

  /* ================================================================
     FAQ accordion. Justification: state transition.
     ================================================================ */

  function initFaq(root) {
    root = root || document;
    root.querySelectorAll(".faq__q:not([data-bound])").forEach(function (btn) {
      btn.setAttribute("data-bound", "1");
      btn.addEventListener("click", function () {
        var item = btn.closest(".faq__item");
        var panel = item.querySelector(".faq__a");
        var open = item.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
        if (reduced || !gsap) {
          panel.style.height = open ? "auto" : "0";
          return;
        }
        if (open) {
          gsap.set(panel, { height: "auto" });
          gsap.from(panel, { height: 0, duration: 0.4, ease: "power2.out" });
        } else {
          gsap.to(panel, { height: 0, duration: 0.32, ease: "power2.in" });
        }
      });
    });
  }

  /* ================================================================
     Size guide toggle
     ================================================================ */

  function initSizeGuide(root) {
    root = root || document;
    root.querySelectorAll("[data-size-guide-toggle]:not([data-bound])").forEach(function (btn) {
      btn.setAttribute("data-bound", "1");
      btn.addEventListener("click", function () {
        var id = btn.getAttribute("data-size-guide-toggle");
        var panel = document.getElementById(id);
        if (!panel) return;
        var show = panel.hidden;
        panel.hidden = !show;
        btn.setAttribute("aria-expanded", show ? "true" : "false");
        btn.textContent = show ? btn.dataset.labelClose : btn.dataset.labelOpen;
      });
    });
  }

  /* ================================================================
     Gallery
     ================================================================ */

  function initGallery(root) {
    root = root || document;
    root.querySelectorAll("[data-gallery]:not([data-bound])").forEach(function (gal) {
      gal.setAttribute("data-bound", "1");
      var main = gal.querySelector("[data-gallery-main]");
      gal.querySelectorAll(".gallery__thumb").forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          var src = thumb.getAttribute("data-full");
          var alt = thumb.getAttribute("data-alt") || "";
          if (!src || !main) return;
          /* srcset takes precedence over src, so a responsive image will
             keep showing the old picture no matter what src is set to.
             Clear it before swapping. */
          main.removeAttribute("srcset");
          main.removeAttribute("sizes");
          main.src = src;
          main.alt = alt;
          gal.querySelectorAll(".gallery__thumb").forEach(function (t) {
            t.setAttribute("aria-current", t === thumb ? "true" : "false");
          });
        });
      });
    });
  }

  /* Show the media that matches a chosen variant, when it has one. */
  function showVariantImage(form, variant) {
    if (!variant || !variant.featured_image) return;
    var gal = document.querySelector("[data-gallery]");
    if (!gal) return;
    var main = gal.querySelector("[data-gallery-main]");
    if (!main) return;
    var target = variant.featured_image.src;
    // same srcset precedence trap as the thumbnail swap
    main.removeAttribute("srcset");
    main.removeAttribute("sizes");
    main.src = target;
    gal.querySelectorAll(".gallery__thumb").forEach(function (t) {
      var match = t.getAttribute("data-full") &&
        t.getAttribute("data-full").split("?")[0] === target.split("?")[0];
      t.setAttribute("aria-current", match ? "true" : "false");
    });
  }

  /* ================================================================
     Variant picker

     Reads the product's variant JSON, resolves the selected option
     combination, and updates price, availability and the buy button.
     ================================================================ */

  function initVariantForms(root) {
    root = root || document;
    root.querySelectorAll("[data-variant-form]:not([data-bound])").forEach(function (form) {
      form.setAttribute("data-bound", "1");

      var dataEl = document.getElementById(form.getAttribute("data-variant-json"));
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); }
      catch (e) { return; }

      var idInput = form.querySelector("[data-variant-id]");
      var priceEl = form.querySelector("[data-price]");
      var compareEl = form.querySelector("[data-compare-price]");
      var submit = form.querySelector("[data-add]");
      var barPrice = document.querySelector("[data-buybar-price]");

      function selected() {
        var opts = [];
        form.querySelectorAll("[data-option-index]").forEach(function (grp) {
          var i = parseInt(grp.getAttribute("data-option-index"), 10);
          var on = grp.querySelector('.swatch[aria-pressed="true"]');
          var v = on ? on.getAttribute("data-value") : null;
          /* An artist from the roster with no matching option value carries
             data-value="". Treat that as "no constraint" rather than as a
             literal empty option, or nothing would ever resolve. */
          opts[i] = v ? v : null;
        });
        return opts;
      }

      function match(opts) {
        return variants.filter(function (v) {
          return opts.every(function (o, i) { return o === null || v.options[i] === o; });
        })[0] || null;
      }

      // Grey out combinations that cannot be bought with the current choices.
      function refreshAvailability(opts) {
        form.querySelectorAll("[data-option-index]").forEach(function (grp) {
          var i = parseInt(grp.getAttribute("data-option-index"), 10);
          grp.querySelectorAll(".swatch").forEach(function (sw) {
            var val = sw.getAttribute("data-value");

            /* A roster artist with no matching Shopify option value carries
               data-value="". That is "this choice puts no constraint on the
               variant", not "match a variant whose option is empty".
               Treating it literally marked every such artist as sold out,
               which is why MARKO and Julia Alexa came up greyed and struck
               through. Attribution for them rides on the line item property
               instead, so they are always purchasable. */
            if (!val) {
              sw.classList.remove("swatch--unavailable");
              return;
            }

            var probe = opts.slice();
            probe[i] = val;
            var any = variants.some(function (v) {
              return v.available && probe.every(function (o, k) {
                return o === null || v.options[k] === o;
              });
            });
            sw.classList.toggle("swatch--unavailable", !any);
          });
        });
      }

      function update() {
        var opts = selected();
        var variant = match(opts);

        form.querySelectorAll("[data-option-index]").forEach(function (grp) {
          var out = grp.querySelector("[data-picker-value]");
          var on = grp.querySelector('.swatch[aria-pressed="true"]');
          if (!out) return;
          /* data-label is the artist's own spelling from the API; fall back
             to the raw Shopify option value for every other picker. */
          out.textContent = on
            ? (on.getAttribute("data-label") || on.getAttribute("data-value"))
            : "";
        });

        refreshAvailability(opts);

        if (!variant) {
          if (submit) { submit.disabled = true; submit.textContent = submit.dataset.labelUnavailable; }
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = money(variant.price);
        if (barPrice) barPrice.textContent = money(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = money(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available
            ? submit.dataset.labelAdd
            : submit.dataset.labelSoldOut;
        }

        showVariantImage(form, variant);

        // keep the URL shareable with the chosen variant
        if (history.replaceState && variant.id) {
          var url = new URL(window.location.href);
          url.searchParams.set("variant", variant.id);
          history.replaceState({}, "", url.toString());
        }
      }

      /* Delegated: the artist group's swatches are rebuilt from the live
         roster after this runs, so binding each button directly would miss
         every artist that is not a Shopify option value. */
      form.addEventListener("click", function (e) {
        var sw = e.target.closest(".swatch");
        if (!sw || !form.contains(sw)) return;
        var grp = sw.closest("[data-option-index]");
        if (!grp) return;
        grp.querySelectorAll(".swatch").forEach(function (o) {
          o.setAttribute("aria-pressed", o === sw ? "true" : "false");
        });
        if (grp.querySelector("[data-artist-options]") || grp.hasAttribute("data-artist-options") ||
            grp.querySelector(".swatch--artist")) {
          var g = grp.querySelector("[data-artist-options]") || grp;
          syncArtistProperty(g);
        }
        update();
      });

      form.addEventListener("submit", function (e) {
        e.preventDefault();
        addToCart(form, submit);
      });

      update();
    });
  }

  /* ================================================================
     PARTNERED ARTISTS

     The Shopify option values ("Christian Gates") and the API display
     names ("CHRI$TIAN GATE$") are not the same string, so matching is
     done on a normalised key: lowercase, $ to s, o-slash to o, then
     everything non-alphanumeric removed. That maps both spellings onto
     "christiangates".

     Only variants that actually exist can be bought, so this never adds
     or removes swatches. It decorates the ones Shopify rendered.
     ================================================================ */

  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* Latin letters carrying a stroke or ligature do NOT decompose under
     NFKD, so "ø" survives normalisation and then gets stripped as if it
     were punctuation. That is how MARKO used to slugify to "mark".
     Transliterate them explicitly, before normalising. */
  var TRANSLITERATE = {
    "ø": "o", "đ": "d", "ð": "d", "ł": "l", "ħ": "h", "ŧ": "t",
    "æ": "ae", "œ": "oe", "ß": "ss", "þ": "th"
  };

  function transliterate(s) {
    return String(s || "").toLowerCase().replace(/[øđðłħŧæœßþ]/g, function (c) {
      return TRANSLITERATE[c] || c;
    });
  }

  /* Comparison key. Used only to line an API artist up with a Shopify
     option value, never displayed. */
  function artistKey(name) {
    return transliterate(name)
      .replace(/\$/g, "s")
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]/g, "");
  }

  /* URL slug for /artists/:slug on the landing site.
     "$" is kept deliberately: stage names built around it turn to mush if
     it is stripped like ordinary punctuation. */
  function artistSlug(name) {
    return transliterate(name)
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9$]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  var artistIndex = Object.create(null);
  var artistOverrides = null;

  /* Theme settings, Artist images. Keyed by the same normalised key as
     everything else, so the merchant can type "Christian Gates" and still
     match "CHRI$TIAN GATE$". */
  function overrideFor(name) {
    if (artistOverrides === null) {
      artistOverrides = Object.create(null);
      var raw = readJson("ArtistOverrides") || {};
      Object.keys(raw).forEach(function (k) {
        var key = artistKey(k);
        if (key && raw[k]) artistOverrides[key] = raw[k];
      });
    }
    return artistOverrides[artistKey(name)] || null;
  }

  function indexArtists(list) {
    (list || []).forEach(function (a) {
      var key = artistKey(a.name);
      if (!key) return;
      var override = overrideFor(a.name);
      if (override) {
        /* merchant upload wins outright; keep the previous best as the
           next thing to try if that upload ever 404s */
        a = Object.assign({}, a, {
          image: override,
          imageFallback: a.image || a.imageFallback || ""
        });
      }
      artistIndex[key] = a;
    });
  }

  function initials(name) {
    var clean = String(name || "").replace(/\$/g, "S").replace(/[^\p{L}\p{N} ]/gu, " ");
    var parts = clean.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /* Artist artwork is hosted on the landing site and on third-party CDNs,
     neither of which this theme controls. A path that is missing can also
     come back as 200 text/html from a SPA catch-all rather than a clean
     404, so "it responded" is not proof of an image. Every artist image is
     therefore probed with Image() and only applied on a real decode.
     Anything that fails degrades to a lettermark instead of a broken box. */
  function loadImage(src) {
    return new Promise(function (resolve) {
      if (!src) return resolve(false);
      var img = new Image();
      img.onload = function () { resolve(img.naturalWidth > 0); };
      img.onerror = function () { resolve(false); };
      img.src = src;
    });
  }

  /* Try the bundled theme asset first, then the landing copy. Returns the
     first URL that actually decodes, or null. */
  function resolveArtistImage(a) {
    if (!a) return Promise.resolve(null);
    var candidates = [a.image, a.imageFallback].filter(Boolean);
    if (!candidates.length) return Promise.resolve(null);

    return candidates.reduce(function (chain, src) {
      return chain.then(function (found) {
        if (found) return found;
        return loadImage(src).then(function (ok) { return ok ? src : null; });
      });
    }, Promise.resolve(null));
  }

  /* Build a swatch for every partnered artist, not just the ones that
     happen to exist as Shopify option values.

     Attribution is recorded as a line item property, so the roster can
     grow without multiplying variants. When the chosen artist DOES match
     an option value the variant is selected too, which keeps existing
     orders and reporting intact. When it does not, the variant is left
     alone (the artist option has no effect on price or stock) and the
     property carries the real answer. */
  function buildArtistPicker(root) {
    root = root || document;
    var roster = Object.keys(artistIndex).map(function (k) { return artistIndex[k]; });
    if (!roster.length) return;

    root.querySelectorAll("[data-artist-options]:not([data-roster-built])").forEach(function (group) {
      group.setAttribute("data-roster-built", "1");

      var optionValues = [];
      group.querySelectorAll(".swatch").forEach(function (sw) {
        optionValues.push(sw.getAttribute("data-value"));
      });
      var selectedKey = artistKey(
        (group.querySelector('.swatch[aria-pressed="true"]') || {}).getAttribute
          ? group.querySelector('.swatch[aria-pressed="true"]').getAttribute("data-value")
          : ""
      );

      var frag = document.createDocumentFragment();
      roster.forEach(function (a) {
        var key = artistKey(a.name);
        var match = optionValues.filter(function (v) { return artistKey(v) === key; })[0] || "";

        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "swatch swatch--artist";
        btn.setAttribute("data-value", match);          // "" when not a variant
        btn.setAttribute("data-label", a.name);
        btn.setAttribute("data-artist", a.name);
        btn.setAttribute("aria-pressed", key === selectedKey ? "true" : "false");

        var av = document.createElement("span");
        av.className = "swatch__avatar";
        av.setAttribute("aria-hidden", "true");

        var nm = document.createElement("span");
        nm.className = "swatch__name";
        nm.textContent = a.name;

        btn.appendChild(av);
        btn.appendChild(nm);
        frag.appendChild(btn);
      });

      group.textContent = "";
      group.appendChild(frag);

      // nothing preselected (roster order changed): take the first
      if (!group.querySelector('.swatch[aria-pressed="true"]')) {
        var first = group.querySelector(".swatch");
        if (first) first.setAttribute("aria-pressed", "true");
      }

      syncArtistProperty(group);
    });
  }

  /* Mirror the chosen artist into the hidden line item property input. */
  function syncArtistProperty(group) {
    var form = group.closest("[data-variant-form]");
    if (!form) return;
    var input = form.querySelector("[data-artist-property]");
    var on = group.querySelector('.swatch[aria-pressed="true"]');
    if (input && on) input.value = on.getAttribute("data-artist") || on.getAttribute("data-value") || "";
  }

  function paintArtistSwatches(root) {
    root = root || document;
    root.querySelectorAll("[data-artist-options] .swatch--artist").forEach(function (sw) {
      var a = artistIndex[artistKey(sw.getAttribute("data-artist") || sw.getAttribute("data-value"))];
      var av = sw.querySelector(".swatch__avatar");
      if (!a) return;

      /* The artist's name is theirs. Show it exactly as the API spells it
         ("CHRI$TIAN GATE$"), not as the Shopify option happens to be typed.
         data-value keeps the Shopify string because that is what resolves
         the variant; data-label carries what the customer reads. */
      if (a.name) {
        var nameEl = sw.querySelector(".swatch__name");
        if (nameEl && nameEl.textContent !== a.name) nameEl.textContent = a.name;
        sw.setAttribute("data-label", a.name);

        var form = sw.closest("[data-variant-form]");
        if (form && sw.getAttribute("aria-pressed") === "true") {
          var grp = sw.closest("[data-option-index]");
          var out = grp && grp.querySelector("[data-picker-value]");
          if (out) out.textContent = a.name;
        }
      }

      if (!av) return;
      if (av.getAttribute("data-src") === (a.image || "")) return;
      av.setAttribute("data-src", a.image || "");

      resolveArtistImage(a).then(function (src) {
        if (src) {
          av.style.backgroundImage = 'url("' + src + '")';
          av.textContent = "";
          av.classList.remove("swatch__avatar--fallback");
        } else {
          av.style.backgroundImage = "";
          av.textContent = initials(a.name);
          av.classList.add("swatch__avatar--fallback");
        }
      });
    });
  }

  /* Cards are rendered server-side with a best-guess URL. Re-resolve them
     here so a merchant upload (or the live API) wins over whatever Liquid
     guessed, and so a dead URL becomes a lettermark instead of a gap. */
  function hydrateArtistCards(root) {
    root = root || document;
    root.querySelectorAll("[data-artist-card]:not([data-img-guarded])").forEach(function (card) {
      card.setAttribute("data-img-guarded", "1");

      var img = card.querySelector("img");
      var h = card.querySelector("h3");
      var name = h ? h.textContent.trim() : "";
      var a = artistIndex[artistKey(name)];

      function lettermark() {
        if (img) img.remove();
        if (card.querySelector(".artist__initials")) return;
        var mark = document.createElement("span");
        mark.className = "artist__initials";
        mark.setAttribute("aria-hidden", "true");
        mark.textContent = initials(name);
        card.insertBefore(mark, card.firstChild);
        card.classList.add("artist--noimg");
      }

      // no roster entry: just guard whatever Liquid rendered
      if (!a) {
        if (!img) return lettermark();
        if (img.complete) { if (!img.naturalWidth) lettermark(); }
        else {
          img.addEventListener("error", lettermark);
          img.addEventListener("load", function () { if (!img.naturalWidth) lettermark(); });
        }
        return;
      }

      resolveArtistImage(a).then(function (src) {
        if (!src) return lettermark();
        if (!img) {
          img = document.createElement("img");
          img.alt = name;
          img.loading = "lazy";
          card.insertBefore(img, card.firstChild);
          card.classList.remove("artist--noimg");
        }
        if (img.getAttribute("src") !== src) img.setAttribute("src", src);
      });
    });
  }

  /* Live refresh. Guarded on every axis: the setting has to be on, the
     endpoint reachable, and CORS permitted from this origin. Any failure
     leaves the baked roster in place, which is why it is baked. */
  function refreshArtists() {
    var cfg = readJson("ArtistConfig");
    if (!cfg || !cfg.live || !cfg.endpoint) return Promise.resolve();

    var headers = { Accept: "application/json" };
    if (cfg.apiKey) headers["x-api-key"] = cfg.apiKey;

    return fetch(cfg.endpoint, { headers: headers })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.artists) return;

        var landing = (cfg.landing || "").replace(/\/+$/, "");

        /* derive the assets folder from a known asset URL by removing the
           filename and cache-busting query */
        var assetBase = "";
        if (cfg.assetSample) {
          assetBase = cfg.assetSample.split("?")[0].replace(/artist-sample\.png$/, "");
        }

        var mapped = data.artists.map(function (a) {
          var slug = artistSlug(a.displayname);
          var raw = a.imageurl || "";
          var absolute = /^https?:\/\//.test(raw);

          var image = null;
          var imageFallback = "";

          if (absolute) {
            image = raw;
          } else if (raw) {
            /* A bare slug is NOT a URL. Liquid already resolved these to a
               bundled theme asset, so reuse that rather than rebuilding a
               landing URL here.

               This is what the first version got wrong: it replaced a
               working assets/artist-<slug>.png with a landing URL, so a
               successful API refresh silently broke the photos that had
               just rendered. That is also why the theme editor looked
               right and the preview did not, the editor was not
               completing the fetch. */
            var known = artistIndex[artistKey(a.displayname)];
            if (known && known.image) {
              image = known.image;
              imageFallback = known.imageFallback || (landing + "/artists/" + raw + ".png");
            } else {
              /* an artist partnered since this theme was built: try the
                 same asset naming first, then the landing copy */
              image = assetBase ? assetBase + "artist-" + raw + ".png" : "";
              imageFallback = landing + "/artists/" + raw + ".png";
              if (!image) { image = imageFallback; imageFallback = ""; }
            }
          }

          return {
            name: a.displayname,
            slug: slug,
            image: image,
            imageFallback: imageFallback,
            url: landing + "/artists/" + slug,
            fans: a.fanscount
          };
        });

        indexArtists(mapped);
        /* let the cards re-resolve against the fresh roster */
        document.querySelectorAll("[data-artist-card][data-img-guarded]").forEach(function (c) {
          c.removeAttribute("data-img-guarded");
        });
        hydrateArtistCards(document);
        paintArtistSwatches(document);
        decorateArtistCards(mapped);
      })
      .catch(function () {
        /* offline, blocked or rotated key: baked roster stands */
      });
  }

  function decorateArtistCards(list) {
    var grid = document.querySelector("[data-artists-grid]");
    if (!grid) return;
    var byKey = Object.create(null);
    list.forEach(function (a) { byKey[artistKey(a.name)] = a; });

    grid.querySelectorAll("[data-artist-card]").forEach(function (card) {
      var h = card.querySelector("h3");
      if (!h) return;
      var a = byKey[artistKey(h.textContent)];
      if (!a) return;
      var fans = card.querySelector("[data-artist-fans]");
      if (fans && typeof a.fans === "number") {
        fans.textContent = a.fans.toLocaleString() + " fans";
        fans.hidden = false;
      }
    });
  }

  function initArtists(root) {
    if (!Object.keys(artistIndex).length) indexArtists(readJson("ArtistRoster"));
    hydrateArtistCards(root);
    buildArtistPicker(root);
    paintArtistSwatches(root);
  }

  /* Store badges are bundled theme assets. If they were not copied in
     before zipping, the <img> 404s and the section would show two broken
     boxes on the homepage. Swap in the text link instead: the destination
     still works, which is the part that matters. */
  function guardStoreBadges(root) {
    root = root || document;
    root.querySelectorAll("[data-store-badge]:not([data-badge-guarded])").forEach(function (a) {
      a.setAttribute("data-badge-guarded", "1");
      var img = a.querySelector("img");
      var fallback = a.querySelector(".appcta__fallback");
      if (!img || !fallback) return;

      function fail() {
        img.remove();
        fallback.hidden = false;
      }

      if (img.complete) {
        if (!img.naturalWidth) fail();
      } else {
        img.addEventListener("error", fail);
        img.addEventListener("load", function () { if (!img.naturalWidth) fail(); });
      }
    });
  }

  /* ================================================================
     Quantity steppers on the buy form (the cart page has its own,
     because those have to round-trip to Shopify on every change)
     ================================================================ */

  function initQtyAdjust(root) {
    root = root || document;
    root.querySelectorAll("[data-qty-adjust]:not([data-bound])").forEach(function (btn) {
      btn.setAttribute("data-bound", "1");
      btn.addEventListener("click", function () {
        var wrap = btn.closest(".qty");
        var input = wrap && wrap.querySelector("[data-qty]");
        if (!input) return;
        var step = parseInt(btn.getAttribute("data-qty-adjust"), 10);
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      });
    });
  }

  /* ================================================================
     Cart
     ================================================================ */

  function toast(msg) {
    var el = document.getElementById("CartToast");
    if (!el) return;
    el.querySelector(".cart-toast__text").textContent = msg;
    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add("is-visible"); });
    clearTimeout(el._t);
    el._t = setTimeout(function () {
      el.classList.remove("is-visible");
      setTimeout(function () { el.hidden = true; }, 500);
    }, 4000);
  }

  function updateCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function addToCart(form, submit) {
    var original = submit ? submit.textContent : "";
    if (submit) { submit.disabled = true; submit.textContent = submit.dataset.labelAdding; }

    fetch(window.shopRoutes.cart_add, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{
          id: parseInt(form.querySelector("[data-variant-id]").value, 10),
          quantity: parseInt((form.querySelector("[data-qty]") || { value: 1 }).value, 10) || 1
        }]
      })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body.description || res.body.message || "Could not add to cart");
        return fetch(window.shopRoutes.cart + ".js").then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        toast(form.getAttribute("data-added-message") || "Added to cart");
        if (submit) { submit.disabled = false; submit.textContent = original; }
      })
      .catch(function (err) {
        toast(err.message);
        if (submit) { submit.disabled = false; submit.textContent = original; }
      });
  }

  function initCartPage(root) {
    root = root || document;
    root.querySelectorAll("[data-cart-line]:not([data-bound])").forEach(function (row) {
      row.setAttribute("data-bound", "1");
      var key = row.getAttribute("data-cart-line");
      var input = row.querySelector("[data-line-qty]");

      function change(qty) {
        fetch(window.shopRoutes.cart_change, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: key, quantity: qty })
        })
          .then(function (r) { return r.json(); })
          .then(function () { window.location.reload(); })
          .catch(function () { toast("Could not update the cart"); });
      }

      row.querySelectorAll("[data-qty-step]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var step = parseInt(btn.getAttribute("data-qty-step"), 10);
          var next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
          input.value = next;
          change(next);
        });
      });

      if (input) {
        input.addEventListener("change", function () {
          change(Math.max(0, parseInt(input.value, 10) || 0));
        });
      }

      var remove = row.querySelector("[data-line-remove]");
      if (remove) {
        remove.addEventListener("click", function (e) { e.preventDefault(); change(0); });
      }
    });
  }

  /* ================================================================
     Sticky mobile buy bar.
     Appears once the real buy button has scrolled out of view.
     ================================================================ */

  function initBuyBar(root) {
    root = root || document;
    var bar = root.querySelector(".buybar");
    var anchor = root.querySelector("[data-buybar-anchor]");
    if (!bar || !anchor || bar.hasAttribute("data-bound")) return;
    bar.setAttribute("data-bound", "1");

    if (!("IntersectionObserver" in window)) { bar.classList.add("is-visible"); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle("is-visible", !entry.isIntersecting);
      });
    }, { rootMargin: "-120px 0px 0px 0px" });
    io.observe(anchor);

    var barBtn = bar.querySelector("[data-buybar-add]");
    if (barBtn) {
      barBtn.addEventListener("click", function () {
        var form = document.querySelector("[data-variant-form]");
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event("submit", { cancelable: true }));
      });
    }
  }

  /* ================================================================
     Boot
     ================================================================ */

  function initAll(root) {
    initHeader(root);
    initReveals(root);
    initStaggers(root);
    initTimeline(root);
    initFaq(root);
    initGallery(root);
    initSizeGuide(root);
    initVariantForms(root);
    initQtyAdjust(root);
    initCartPage(root);
    initBuyBar(root);
    initArtists(root);
    guardStoreBadges(root);
  }

  function boot() {
    initLenis();
    initAll(document);
    document.documentElement.classList.add("motion-ready");
    refreshArtists();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Fonts swap late and reflow headings; ScrollTrigger measured the old
  // layout. Re-measure once they land.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { if (ScrollTrigger) ScrollTrigger.refresh(); });
  }

  document.addEventListener("shopify:section:load", function (e) {
    killSection(e.detail.sectionId);
    initAll(e.target);
    if (ScrollTrigger) ScrollTrigger.refresh();
  });

  document.addEventListener("shopify:section:unload", function (e) {
    killSection(e.detail.sectionId);
  });

  document.addEventListener("shopify:section:select", function () {
    if (lenis) lenis.stop();
  });
})();
